import React, { useState } from 'react';
import { useShop } from '../context/ShopContext';
import {
  ShoppingBag,
  Menu,
  X,
  Search,
  Lock,
  Download,
  Sparkles
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Logo } from './Logo';

export const Header: React.FC = () => {
  const {
    activePage,
    setActivePage,
    basket,
    setIsBasketOpen,
    isAdminLoggedIn,
    searchQuery,
    setSearchQuery,
    deferredInstallPrompt,
    installPWA
  } = useShop();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const totalBasketCount = basket.reduce((sum, item) => sum + item.quantity, 0);

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'shop', label: 'Browse Uniform' },
    { id: 'manage-reservation', label: 'Find My Reservation' },
    { id: 'donate', label: 'Donate Uniform' },
    { id: 'how-it-works', label: 'How It Works' },
    { id: 'news', label: 'News & Updates' },
    { id: 'about', label: 'About Us' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contact', label: 'Contact' }
  ];

  const handleNavClick = (pageId: string) => {
    setActivePage(pageId);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white/90 backdrop-blur-xl text-slate-900 shadow-sm border-b border-slate-200/80 transition-all print:hidden">
      {/* Top Banner */}
      <div className="bg-[#002366] py-2 px-4 text-xs font-medium text-center text-white flex items-center justify-center gap-2">
        <span className="inline-flex items-center gap-1 bg-emerald-500/20 text-emerald-300 text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider border border-emerald-400/30">
          100% Free
        </span>
        <span className="font-heading font-medium">Free School Uniform for Immingham Families</span>
        <span className="hidden sm:inline-block text-blue-200">• Bert Boyden Community Centre</span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => handleNavClick('home')}
            className="text-left focus:outline-none"
          >
            <Logo variant="horizontal" size="md" />
          </button>

          {/* Desktop Navigation Links */}
          <nav className="hidden xl:flex items-center gap-1">
            {navItems.map((item) => {
              const isActive = activePage === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`px-3.5 py-2 rounded-full text-xs font-heading font-bold transition-all relative ${
                    isActive
                      ? 'text-[#002366] bg-blue-50/80 border border-blue-200/60 shadow-sm'
                      : 'text-slate-600 hover:text-[#002366] hover:bg-slate-100/80'
                  }`}
                >
                  {item.label}
                  {isActive && (
                    <motion.div
                      layoutId="activeNavIndicator"
                      className="absolute bottom-0 left-3 right-3 h-0.5 bg-blue-600 rounded-full"
                    />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Quick Search */}
            <div className="hidden md:flex items-center relative max-w-[180px] sm:max-w-[210px]">
              <Search className="w-4 h-4 absolute left-3 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search uniforms..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (activePage !== 'shop') setActivePage('shop');
                }}
                className="w-full pl-9 pr-3 py-1.5 bg-slate-100/90 border border-slate-200/90 rounded-full text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-600 focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all"
              />
            </div>

            {/* PWA Install Button */}
            {deferredInstallPrompt && (
              <button
                onClick={installPWA}
                className="hidden sm:flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 text-xs font-bold transition shadow-sm"
                title="Install app to Home Screen"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Install</span>
              </button>
            )}

            {/* Volunteer Hub / Admin Button */}
            <button
              onClick={() => handleNavClick('admin')}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-heading font-bold border transition-all ${
                isAdminLoggedIn
                  ? 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100 shadow-sm'
                  : 'bg-white border-blue-600/30 text-blue-800 hover:bg-blue-50 hover:border-blue-600 shadow-sm'
              }`}
            >
              <Lock className="w-3.5 h-3.5 text-blue-600" />
              <span className="hidden sm:inline">{isAdminLoggedIn ? 'Volunteer Hub' : 'Volunteer Hub'}</span>
            </button>

            {/* Reservation Basket Trigger */}
            <button
              onClick={() => setIsBasketOpen(true)}
              className="relative flex items-center justify-center px-4 py-2 rounded-full bg-gradient-to-r from-[#002366] to-blue-600 hover:from-blue-900 hover:to-blue-700 text-white font-heading font-bold text-xs shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-95 transition-all gap-2"
              aria-label="View Reservation Basket"
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Basket</span>
              {totalBasketCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="bg-sky-400 text-slate-950 font-extrabold text-[11px] px-2 py-0.5 rounded-full shadow-sm"
                >
                  {totalBasketCount}
                </motion.span>
              )}
            </button>

            {/* Mobile menu toggle button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="xl:hidden p-2.5 rounded-2xl text-slate-700 hover:bg-slate-100 transition"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-white/95 border-t border-slate-200 px-4 py-4 space-y-3 backdrop-blur-xl"
          >
            {/* Mobile Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input
                type="text"
                placeholder="Search uniform items..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (activePage !== 'shop') setActivePage('shop');
                }}
                className="w-full pl-9 pr-3 py-2 bg-slate-100 border border-slate-200 rounded-full text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-blue-600"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 pt-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`px-3 py-2.5 rounded-2xl text-xs font-heading font-bold text-left transition ${
                    activePage === item.id
                      ? 'bg-[#002366] text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {deferredInstallPrompt && (
              <button
                onClick={() => {
                  installPWA();
                  setMobileMenuOpen(false);
                }}
                className="w-full mt-2 flex items-center justify-center gap-2 py-2.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-xs font-bold shadow-sm"
              >
                <Download className="w-4 h-4" />
                Add Uniform Loop to Home Screen
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
